import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type { WidgetControl, ReactionGroup } from '@examdojo/core/widget';
import type { CoordinateSystemConfig } from '@examdojo/core/widget';
import type { GraphpadDisplayConfig, GraphpadParams, GraphpadWidgetConfig } from '../graphpad.model';
import { ElementListSectionComponent } from './sections/element-list-section.component';

@Component({
  selector: 'dojo-graphpad-editor',
  standalone: true,
  imports: [ElementListSectionComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './graphpad-editor.component.html',
  styleUrl: './graphpad-editor.component.scss',
  host: { class: 'block h-full' },
})
export class GraphpadEditorComponent {
  readonly config = input.required<GraphpadWidgetConfig>();
  readonly configChange = output<GraphpadWidgetConfig>();

  readonly params = computed(() => this.config().params);
  readonly display = computed(() => {
    const d = this.config().display;
    // Surface effective canvas defaults so the editor reflects actual widget behavior:
    // graphpad uses 'neutral-50' background by default
    return {
      ...(d ?? {}),
      canvas: {
        ...(d?.canvas ?? {}),
        color: d?.canvas?.color ?? 'neutral-50',
      },
    };
  });
  readonly coordSystem = computed(() => {
    const cs = this.config().display?.coordinateSystem;
    // Surface effective defaults so the editor reflects actual widget behavior:
    // graphpad hides grid by default (axes and ticks are shown by default)
    return {
      ...(cs ?? {}),
      grid: cs?.grid ?? { visible: false },
    };
  });
  readonly behaviorControls = computed(() => this.config().behavior?.controls ?? []);
  readonly reactions = computed(() => this.config().behavior?.reactions ?? []);

  onCoordSystemChange(coordSystem: CoordinateSystemConfig): void {
    this.emitUpdate((draft) => {
      draft.display = { ...(draft.display ?? {}), coordinateSystem: coordSystem };
    });
  }

  onParamsChange(params: GraphpadParams): void {
    this.emitUpdate((draft) => {
      draft.params = params;
    });
  }

  onDisplayChange(display: GraphpadDisplayConfig): void {
    this.emitUpdate((draft) => {
      draft.display = { ...(draft.display ?? {}), ...display };
    });
  }

  onBehaviorControlsChange(controls: WidgetControl[]): void {
    this.emitUpdate((draft) => {
      draft.behavior = { ...(draft.behavior ?? {}), controls };
    });
  }

  onReactionsChange(reactions: ReactionGroup[]): void {
    this.emitUpdate((draft) => {
      draft.behavior = { ...(draft.behavior ?? {}), reactions: reactions.length ? reactions : undefined };
    });
  }

  private emitUpdate(mutate: (draft: GraphpadWidgetConfig) => void): void {
    const draft = structuredClone(this.config());
    mutate(draft);
    this.configChange.emit(draft);
  }
}
